/**
 * 비교용 정규화 (TRANSCRIPT_PROTOCOL §5). RAW는 바꾸지 않고, 비교할 때만 쓴다.
 * 완화: 문장부호, 띄어쓰기, 단독 추임새, 일부 조사·구어 변형.
 * 보존: 부정어, 숫자, 기간·횟수, 인물/관계, 약물, 위험 표현 — 이들은 토큰으로 남겨 차이를 탐지한다.
 */

const FILLERS = new Set(['어', '음', '아', '그', '저', '이제', '뭐', '좀', '막', '약간', '네', '예', '응']);

const VARIANTS: [RegExp, string][] = [
  [/한테/g, '에게'],
  [/^그니까/g, '그러니까'],
  [/^근데/g, '그런데'],
];

/** 원문 문자열 → 비교용 토큰 (원문 위치 포함) */
export type Token = { norm: string; start: number; end: number };

export function tokenize(text: string): Token[] {
  const out: Token[] = [];
  const re = /[0-9A-Za-z가-힣]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    let t = m[0].toLowerCase();
    for (const [from, to] of VARIANTS) t = t.replace(from, to);
    out.push({ norm: t, start: m.index, end: m.index + m[0].length });
  }
  return out;
}

/** 내용 토큰 (추임새 제외) */
export function contentTokens(text: string): Token[] {
  return tokenize(text).filter((t) => !FILLERS.has(t.norm));
}

export function isFiller(t: string): boolean {
  return FILLERS.has(t);
}

/** 두 토큰열의 최장공통부분열 길이 */
export function lcs(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const prev = new Array<number>(b.length + 1).fill(0);
  const cur = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1]! + 1 : Math.max(prev[j]!, cur[j - 1]!);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j]!;
  }
  return prev[b.length]!;
}

/** 토큰열 유사도 0..1 (Dice 유사 LCS 비율) */
export function similarity(a: string[], b: string[]): number {
  if (!a.length && !b.length) return 1;
  return (2 * lcs(a, b)) / (a.length + b.length);
}

/** 오인식에 강하게: 토큰을 글자 단위로 비교했을 때의 유사도 (조사·받침 차이 완화) */
export function charSimilarity(a: string, b: string): number {
  const x = [...a.replace(/\s+/g, '')];
  const y = [...b.replace(/\s+/g, '')];
  return similarity(x, y);
}
